/**
 * Spot Price & Revenues — the price lens on the Commercial section.
 *
 * 2026-06-10 METIS review: "anything to do with price goes here". The
 * production-by-threshold bands chart moved OUT to Technical Performance
 * (it's a production view); the Price capture vs benchmark chart moved IN
 * from the Commercial overview. What remains:
 *   • Three headline capture cards — capture price by clearing-price band:
 *       all          — every generating interval
 *       RRP > 0      — positive merchant revenue ignoring LGCs
 *       RRP > -LGC   — LGC-net merchant revenue still positive
 *     (editable LGC threshold, defaults to current spot LGC)
 *   • Price capture vs benchmark — asset capture vs regional avg RRP over
 *     the selected window.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import { useThemeColors } from '../theme/ThemeModeContext';
import CommercialWorkspaceTabs from './CommercialWorkspaceTabs';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const fmtMwh = (v) => {
  if (v == null) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} TWh`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} GWh`;
  return `${Math.round(v)} MWh`;
};
const fmtAud = (v) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 2 })}`;
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

const PERIOD_OPTIONS = [
  { code: '7d',  label: '7D'  },
  { code: '30d', label: '30D' },
  { code: '90d', label: '90D' },
  { code: 'ytd', label: 'YTD' },
];

// Three bands — same ordering as the headline cards.
const BANDS = [
  {
    key:        'all',
    label:      'All generation',
    mwhField:   'generation_mwh',
    captureKey: 'weighted_capture_all',
    color:      '#94a3b8',
    desc:       'Baseline: every generating interval',
  },
  {
    key:        'above_zero',
    label:      'RRP > 0',
    mwhField:   'mwh_above_zero',
    captureKey: 'weighted_capture_above_zero',
    color:      '#22d3ee',
    desc:       'Positive merchant revenue (ignoring LGCs)',
  },
  {
    key:        'above_neg_lgc',
    label:      'RRP > -LGC',
    mwhField:   'mwh_above_neg_lgc',
    captureKey: 'weighted_capture_above_neg_lgc',
    color:      '#d98a2b',
    desc:       'LGC-net merchant revenue still positive',
  },
];

function HeadlineCard({ band, mwh, capture, pct }) {
  return (
    <Card variant="outlined" sx={{ flex: '1 1 240px', minWidth: 240 }}>
      <CardContent sx={{ pb: '14px !important', pt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: band.color }} />
          <Typography sx={{
            fontSize: '0.66rem', color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontWeight: 700, flex: 1,
          }}>
            {band.label}
          </Typography>
          {pct != null && (
            <Chip size="small" label={fmtPct(pct)}
              sx={{ height: 18, fontSize: '0.62rem' }} />
          )}
        </Stack>
        <Typography sx={{
          fontSize: '1.35rem', fontWeight: 800,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1.1, mb: 0.25,
        }}>
          {fmtMwh(mwh)}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          {band.desc} · capture {fmtAud(capture)}/MWh
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Price capture vs benchmark ─────────────────────────────────────────────
// Moved in from the Commercial overview (2026-06-10 METIS review). Asset
// capture price vs the time-weighted regional average RRP, auto-bucketed
// by the backend (daily ≤14d, weekly ≤180d, else monthly).
function CaptureBenchmarkChart({ history, fleet, colors, height = 440 }) {
  const series = (history?.series || []).filter((h) => h.month);
  if (!series.length) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.disabled">No capture history for this asset.</Typography>
      </Box>
    );
  }
  const xs    = series.map((h) => new Date(h.month));
  const cap   = series.map((h) => h.asset_capture);
  const flt   = series.map((h) => h.fleet_avg);
  const reg   = series.map((h) => h.region_avg);
  const bucket = history?.bucket_minutes || 43800;
  // Headline benchmark is the same-state, same-fuel fleet capture (carries
  // the shape discount); baseload RRP stays as a faint reference line.
  const hasFleet   = flt.some((v) => v != null);
  const fleetLabel = fleet?.label
    ? (fleet.size ? `${fleet.label} (${fleet.size})` : fleet.label)
    : 'Fleet avg capture';
  return (
    <LineChart
      height={height}
      xAxis={[{
        data: xs,
        scaleType: 'time',
        valueFormatter: (v, ctx) => {
          const d = v instanceof Date ? v : new Date(v);
          const tip = ctx?.location === 'tooltip';
          if (bucket === 1440) {
            return tip
              ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
              : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
          }
          if (bucket === 10080) {
            return tip
              ? `Week of ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
              : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
          }
          return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
        },
        tickLabelStyle: { fontSize: 11, fill: colors.textDim },
      }]}
      yAxis={[{
        valueFormatter: (v) => `A$${Math.round(v)}`,
        tickLabelStyle: { fontSize: 11, fill: colors.textDim },
      }]}
      series={[
        { data: cap, label: 'Asset capture', color: colors.ACCENT,
          valueFormatter: (v) => v == null ? '—' : `A$${v.toFixed(2)}/MWh`,
          showMark: true, connectNulls: true },
        ...(hasFleet ? [{
          data: flt, label: fleetLabel, color: '#22c55e',
          valueFormatter: (v) => v == null ? '—' : `A$${v.toFixed(2)}/MWh`,
          showMark: false, connectNulls: true,
        }] : []),
        // Baseload RRP — faint dashed reference; the gap below it is the
        // shape discount the fleet line already accounts for.
        { id: 'baseload', data: reg, label: 'Region avg RRP (baseload)', color: '#E9B065',
          valueFormatter: (v) => v == null ? '—' : `A$${v.toFixed(2)}/MWh`,
          showMark: false, connectNulls: true },
      ]}
      margin={{ left: 64, right: 24, top: 32, bottom: 36 }}
      slotProps={{ legend: { position: { vertical: 'top', horizontal: 'right' } } }}
      sx={{
        '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
        '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
        '& .MuiChartsGrid-line':  { stroke: colors.hairline },
        '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.75rem' },
        // Baseload is a reference, not a peer — dash it + thin it so the
        // asset and fleet lines read as the comparison that matters.
        '& .MuiLineElement-series-baseload': {
          strokeDasharray: '5 4', strokeWidth: 1.5, opacity: 0.75,
        },
      }}
    />
  );
}

const ClearingPrice = () => {
  const { portfolioId } = useParams();
  const colors = useThemeColors();
  const [portfolio, setPortfolio] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [lgcOverride, setLgcOverride] = useState('');   // empty = use API default
  const [lgcDraft, setLgcDraft] = useState('');
  const [data, setData] = useState(null);
  const [captureData, setCaptureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initial portfolio load → asset picker.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}`)
      .then((r) => {
        if (cancelled) return;
        setPortfolio(r.data);
        const firstAsset = (r.data.assets || [])[0];
        if (firstAsset) setAssetId(firstAsset.asset_id);
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId]);

  // Band totals fetch — drives the headline cards. Daily bucket is fine:
  // the cards only read `totals`.
  useEffect(() => {
    if (!portfolioId || !assetId) return;
    let cancelled = false;
    setData(null);
    const params = { asset_id: assetId, period, bucket: 'daily' };
    if (lgcOverride !== '' && !Number.isNaN(Number(lgcOverride))) {
      params.lgc = Number(lgcOverride);
    }
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/clearing-price`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period, lgcOverride]);

  // Capture vs benchmark fetch — the chart this tab exists for.
  useEffect(() => {
    if (!portfolioId || !assetId) return;
    let cancelled = false;
    setCaptureData(null);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/capture-benchmark`,
              { params: { asset_id: assetId, period } })
      .then((r) => { if (!cancelled) setCaptureData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period]);

  // Keep the LGC text field in sync with the resolved API default until
  // the user types into it. Once they commit a value, lgcOverride drives
  // subsequent fetches and we no longer overwrite the draft.
  useEffect(() => {
    if (lgcOverride === '' && data?.lgc?.aud != null) {
      setLgcDraft(String(data.lgc.aud));
    }
  }, [data?.lgc?.aud, lgcOverride]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={140} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={440} />
      </Box>
    );
  }
  if (error && !portfolio) {
    return <Box sx={{ p: 2.5 }}><Alert severity="error">{error}</Alert></Box>;
  }
  if (!portfolio) return null;
  const assets = portfolio.assets || [];
  if (assets.length === 0) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Alert severity="info">
          This portfolio has no assets. Add assets first.
        </Alert>
      </Box>
    );
  }

  const totals = data?.totals;
  const headline = (b) => {
    if (!totals) return { mwh: null, capture: null, pct: null };
    if (b.key === 'all') {
      return {
        mwh:     totals.generation_mwh,
        capture: totals.weighted_capture_all,
        pct:     totals.generation_mwh ? 100 : null,
      };
    }
    return {
      mwh:     totals[`mwh_${b.key}`],
      capture: totals[`weighted_capture_${b.key}`],
      pct:     totals[`pct_mwh_${b.key}`],
    };
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <CommercialWorkspaceTabs />
      {/* Header strip */}
      <Stack direction="row" alignItems="center" spacing={2}
             sx={{ mb: 2.5, flexWrap: 'wrap', rowGap: 1 }}>
        <PriceCheckIcon sx={{ fontSize: 22, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          Spot Price & Revenues
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {assets.length > 1 && (
          <Select size="small" value={assetId || ''}
                  onChange={(e) => setAssetId(e.target.value)}
                  sx={{ minWidth: 220 }}>
            {assets.map((a) => (
              <MenuItem key={a.asset_id} value={a.asset_id}>
                {a.name} ({a.asset_id})
              </MenuItem>
            ))}
          </Select>
        )}
        <ToggleButtonGroup size="small" value={period} exclusive
                           onChange={(_, v) => v && setPeriod(v)}>
          {PERIOD_OPTIONS.map((p) => (
            <ToggleButton key={p.code} value={p.code} sx={{ px: 1.25 }}>{p.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Threshold for the 'RRP > -LGC' band. Defaults to current spot LGC from aus_warehouse.lgc_price.">
          <TextField
            size="small"
            label="LGC ($/MWh)"
            value={lgcDraft}
            onChange={(e) => setLgcDraft(e.target.value)}
            onBlur={() => {
              if (lgcDraft === '') {
                setLgcOverride('');
              } else if (!Number.isNaN(Number(lgcDraft))) {
                setLgcOverride(lgcDraft);
              }
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ width: 130 }}
          />
        </Tooltip>
      </Stack>

      {/* Headline band cards — capture price by clearing-price band */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        {BANDS.map((b) => {
          const h = headline(b);
          return (
            <HeadlineCard key={b.key} band={b}
                          mwh={h.mwh} capture={h.capture} pct={h.pct} />
          );
        })}
      </Stack>

      {/* Price capture vs benchmark — moved in from the Commercial overview */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Price capture vs benchmark
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Asset capture · {captureData?.fleet?.label || 'fleet avg'} · baseload RRP
              {data?.period?.label ? ` · ${data.period.label}` : ''}
            </Typography>
          </Stack>
          {!captureData ? (
            <Skeleton variant="rectangular" height={440}
                      sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
          ) : (
            <CaptureBenchmarkChart
              history={captureData.capture_history}
              fleet={captureData.fleet}
              colors={colors}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClearingPrice;
